import { Button, Input, Modal } from '@a_team/ui-components';
import {
  BorderColors,
  Breakpoints,
  Colors,
  Icon,
  IconType,
} from '@ateams/components';
import {
  useQueryGenerateTimesheetSummaryBlurb,
  useQueryGetInitiativesByTimesheet,
} from '@src/rq/timesheets';
import { useStores } from '@src/stores';
import { Editor } from '@tiptap/react';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import Skeleton from 'react-loading-skeleton';
import z from 'zod';
import play from './play.svg';
import book from './book.svg';
import { useEditor } from '@src/hooks/useEditor';
import TextEditor from '@src/components/TextEditor';
import { countCharsForEditorValue } from '@src/components/TextEditor/helper';
import { useMission } from '@src/layouts/Mission/NewMissionNavbar/useMission';
import Tag from '../Table/common/Tag';
import cx from 'classnames';
import { MissionBillingPeriod } from '@a_team/models/dist/MissionObject';
import { deduceBillingPeriodFromPaymentCycle } from '../../utils';

interface SummaryProps {
  summaryModalOpen: boolean;
  setSummaryModalOpen: (isOpen: boolean) => void;
  sid: string;
  onContinue: () => void;
}

const useStyles = createUseStyles({
  container: {},
  modal: {
    // first child div padding 0
    width: '680px !important',
    '& > div': {
      padding: 0,
    },
  },
  heroImage: {
    display: 'flex',
    justifyContent: 'center',
  },
  title: {
    fontSize: 20,
    lineHeight: '26px',
    fontWeight: 600,
    textAlign: 'center',
    marginTop: 24,
    marginBottom: 8,
  },
  descWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  desc: {
    maxWidth: 400,
    fontSize: 15,
    lineHeight: '24px',
    textAlign: 'center',
    color: '#818388',
    marginBottom: 0,
    marginTop: 0,
  },
  videoLinkInput: {
    border: '1px solid #DADADC',
    borderColor: '#DADADC !important',
  },
  link: {
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: Colors.secondaryDark,
    fontWeight: 500,
    fontSize: 15,
    lineHeight: '19px',
  },
  modalContent: {
    padding: 15,
  },
  inputLabel: {
    fontSize: 15,
    fontWeight: 500,
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  inputLabelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 40,
    gap: 4,

    '& > span': {
      fontSize: 14,
      color: '#62646A !important',
    },
  },
  subInputLabel: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '19px',
    color: Colors.regularDark,
  },
  subInputLabelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 16,
  },
  optional: {
    color: '#818388',
  },
  btnsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 40,
    padding: 16,
    borderTop: '1px solid #DADADC',
    // select button and width should be fit content and padding 8 12
    '& > button': {
      width: 'fit-content',
      padding: '8px 12px',
    },
  },
  [`@media (min-width: ${Breakpoints.sm}px)`]: {
    modalContent: {
      padding: 40,
      marginBottom: 70,
    },
    btnsContainer: {
      position: 'absolute',
      zIndex: 999,
      bottom: 0,
      width: '100%',
      backgroundColor: '#FFFFFF',
    },
  },
  initiativeTagsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    '& > span': {
      flex: '0 0 auto',
    },
  },
  editorWrapper: {
    '& [class*="textEditorWrapper"]': {
      borderBottom: `none !important`,
      borderRadius: '8px 8px 0px 0px',
    },
  },
  infoTextEditor: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 14,
    padding: 16,
    borderRadius: '0px 0px 8px 8px',
    backgroundColor: '#F9FAFC',
    border: `1px solid ${BorderColors.lighter}`,
  },
  editorWrapperReadOnlyMode: {
    '& [class*="textEditorWrapper"]': {
      border: `1px solid ${BorderColors.lighter} !important`,
      borderRadius: '8px 8px 8px 8px !important',
      padding: '0 16px',
      '& p': {
        marginTop: 15,
        marginBottom: 15,
        fontSize: 15,
        fontWeight: 300,
        lineHeight: '20px',
      },
    },

    '& [class*="infoTextEditor"]': {
      borderRadius: '8px 8px 8px 8px',
      border: 'none !important',
      marginTop: 16,
    },
  },
  icon: { width: 16 },
  previewVideoLink: {
    borderRadius: 8,
    border: `1px solid ${BorderColors.lighter}`,
    padding: 16,
  },
});

export function parseSummaryToHtml(suggestion: string) {
  let parseSuggestion = '';

  const paragraphs = suggestion
    .split('\n')
    .filter((block) => block.trim() !== '');

  if (paragraphs?.length) {
    paragraphs.forEach((paragraph) => {
      parseSuggestion += `<p>${paragraph}</p>`;
    });

    return parseSuggestion;
  }

  return suggestion;
}

enum STEP {
  EDIT = 'EDIT',
  PREVIEW = 'PREVIEW',
}

const Summary = ({
  summaryModalOpen,
  setSummaryModalOpen,
  sid,
  onContinue,
}: SummaryProps): JSX.Element => {
  const styles = useStyles();
  const {
    uiStore,
    missions: { currentMission },
  } = useStores();
  const [step, setStep] = useState<STEP>(STEP.EDIT);

  const [weeks, billingPeriod] = useMemo(() => {
    switch (
      deduceBillingPeriodFromPaymentCycle(currentMission?.selectedPaymentCycle)
    ) {
      case MissionBillingPeriod.Monthly:
        return ['four weeks', 'Monthly'];
      case MissionBillingPeriod.BiWeekly:
        return ['two weeks', 'Bi-Weekly'];
      case MissionBillingPeriod.Weekly:
        return ['one week', 'Weekly'];
      default:
        return ['weeks', undefined];
    }
  }, [currentMission?.selectedPaymentCycle]);

  const { companyName } = useMission();

  const {
    data: initiatives,
    isLoading: isLoadingInitiatives,
    isRefetching: isRefetchingInitiatives,
  } = useQueryGetInitiativesByTimesheet({
    sid,
    enabled: summaryModalOpen,
  });

  const { editor } = useEditor({
    initialValue: '',
    noTitle: true,
    onDescriptionChange: (editor: Editor) => {
      uiStore.setTimesheetDescription(editor.getText());
      uiStore.setTimesheetDescriptionHtml(editor.getHTML());
    },
  });

  const onInsertSuggestion = (suggestion: string) => {
    const contentHTML = parseSummaryToHtml(suggestion);
    // Set the content in the editor
    editor?.commands.setContent(contentHTML);

    // Update the MobX store with the new content
    uiStore.setTimesheetDescription(editor?.getText() || '');
    uiStore.setTimesheetDescriptionHtml(editor?.getHTML() || '');
  };

  const { isLoading } = useQueryGenerateTimesheetSummaryBlurb({
    sid,
    onSuccess: (data) => {
      if (data) {
        uiStore.setOriginalTimesheetDescriptionHtml(
          parseSummaryToHtml(data.suggestion),
        );
        onInsertSuggestion(data.suggestion);
        uiStore.setGptUsageLogIdForTimesheetSummary(data.gptUsageLogId);
      }
    },
    enabled: summaryModalOpen,
  });

  const charCount = countCharsForEditorValue(editor?.getHTML());

  const onClose = () => {
    if (step === STEP.EDIT) {
      setSummaryModalOpen(false);
    } else {
      setStep(STEP.EDIT);
    }
  };

  const handleOnContinue = async () => {
    if (step === STEP.EDIT) {
      setStep(STEP.PREVIEW);
    } else {
      onContinue();
    }
  };

  const isValidUrl = z
    .string()
    .url()
    .optional()
    .safeParse(uiStore.timesheetVideoLink);

  const isEdit = step === STEP.EDIT;

  return (
    <div className={styles.container}>
      <Modal
        variant="slideUp"
        isOpen={summaryModalOpen}
        onClose={onClose}
        className={styles.modal}
        shouldHideGradientStroke
      >
        <div>
          <div className={styles.modalContent}>
            <div className={styles.heroImage}>
              <img src={play} alt="Demo day" />
            </div>

            {isEdit ? (
              <>
                <h4 className={styles.title}>Demo day</h4>
                <div className={styles.descWrapper}>
                  <p className={styles.desc}>
                    Take a moment to reflect on what you've built over the last{' '}
                    {weeks} and share it with the team!
                  </p>
                  <a
                    className={styles.link}
                    target="_blank"
                    href="https://guide.a.team/missions/demo-day-showcase-your-impact"
                    rel="noreferrer"
                  >
                    <img src={book} alt="How to create a good demo" /> How to
                    create a good demo
                  </a>
                </div>
              </>
            ) : (
              <h4 className={styles.title}>
                What is written here will be shared with the team at{' '}
                {companyName}, are you ready to submit?
              </h4>
            )}

            {(isEdit || !!uiStore.timesheetVideoLink) && (
              <div>
                <div className={styles.inputLabelWrapper}>
                  <label className={styles.inputLabel}>
                    <Icon type={IconType.TvBlack} className={styles.icon} />
                    {isEdit ? (
                      <>
                        Add a short video to show off what you built{' '}
                        <span className={styles.optional}> (Optional)</span>
                      </>
                    ) : (
                      'Demo view'
                    )}
                  </label>
                </div>
                <div className={styles.subInputLabelWrapper}>
                  <label className={styles.subInputLabel}>
                    {isEdit ? (
                      <>
                        This is optional, but builders who regularly share
                        videos often stay on missions longer.
                      </>
                    ) : (
                      <>
                        Data shows that builders who consistently include a
                        video update stay on missions longer.
                      </>
                    )}
                  </label>
                </div>
                {isEdit ? (
                  <>
                    <Input
                      error={
                        isValidUrl.success === false &&
                        uiStore.timesheetVideoLink.length > 0
                      }
                      className={styles.videoLinkInput}
                      value={uiStore.timesheetVideoLink}
                      onChange={(e) =>
                        uiStore.setTimesheetVideoLink(e.target.value)
                      }
                      placeholder="Enter a Loom or other video link..."
                      size="stretch"
                    />
                    <small style={{ marginTop: 8 }}>
                      {isValidUrl.success === false &&
                        uiStore.timesheetVideoLink.length > 0 &&
                        'Please enter a valid URL'}
                    </small>
                  </>
                ) : (
                  <div className={styles.previewVideoLink}>
                    {uiStore.timesheetVideoLink}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className={styles.inputLabelWrapper}>
                <label className={styles.inputLabel}>
                  <Icon type={IconType.TagBlack} className={styles.icon} />{' '}
                  Initiatives
                </label>
              </div>
              <div className={styles.subInputLabelWrapper}>
                <label className={styles.subInputLabel}>
                  Initiatives help clients understand the main areas where you
                  dedicated your time. You can edit by adjusting your timesheet
                  entries.
                </label>
              </div>
              <div>
                {isLoadingInitiatives || isRefetchingInitiatives ? (
                  <Skeleton height={28} width="100px" />
                ) : (
                  <div className={styles.initiativeTagsWrapper}>
                    {initiatives?.map((initiative) => (
                      <Tag
                        key={initiative.siid}
                        title={initiative.name}
                        background={initiative.backgroundColor}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div data-testing-id="timesheets-summary-wrapper">
              <div className={styles.inputLabelWrapper}>
                <label className={styles.inputLabel}>
                  <Icon type={IconType.DocumentBlack} className={styles.icon} />{' '}
                  {isEdit ? (
                    <> Share an update with the team at {companyName}</>
                  ) : (
                    <>
                      {billingPeriod ? `${billingPeriod} updates` : 'Updates'}
                    </>
                  )}
                </label>
              </div>
              <div className={styles.subInputLabelWrapper}>
                <label className={styles.subInputLabel}>
                  Discuss what you worked on and the impact it had on the
                  initiatives in your timesheet.
                </label>
              </div>
              {isLoading && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    border: '1px solid #DADADC',
                    padding: 16,
                    borderRadius: 8,
                    marginTop: 8,
                  }}
                >
                  <Skeleton height={30} width="200px" />{' '}
                  <Skeleton height={30} width="100%" />{' '}
                  <Skeleton height={30} width="100%" />{' '}
                  <Skeleton height={30} width="100%" />{' '}
                  <Skeleton height={30} width="100%" />{' '}
                  <Skeleton height={30} width="100%" />{' '}
                </div>
              )}
              {!isLoading && editor && (
                <div
                  className={cx([
                    styles.editorWrapper,
                    { [styles.editorWrapperReadOnlyMode]: !isEdit },
                  ])}
                >
                  <TextEditor
                    editor={editor}
                    hideHeading
                    charCount={charCount}
                    readOnly={!isEdit}
                  />
                  <div className={styles.infoTextEditor}>
                    <Icon type={IconType.InfoBlack} className={styles.icon} />{' '}
                    The information you provide will be shared with{' '}
                    {companyName}.
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={styles.btnsContainer}>
            <Button variant="secondary" onClick={onClose}>
              {isEdit ? 'Close' : 'Back'}
            </Button>
            <Button
              disabled={
                uiStore.timesheetDescription.length === 0 ||
                (uiStore.timesheetVideoLink.length > 0 &&
                  isValidUrl.success === false)
              }
              onClick={handleOnContinue}
              data-testing-id="timesheets-summary-submit-button"
            >
              {isEdit ? 'Continue' : 'Submit'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default observer(Summary);
